import browserPlugin from 'router5-plugin-browser';
import { createRouter, Router as Router5, SubscribeState } from 'router5';
import { Unsubscribe } from 'router5/dist/types/base';
import * as IoC from 'typescript-ioc';
import { IRouter } from '../router';
import { IRuntimeConfiguration } from '../runtime-configuration';


class Router<T> extends IRouter<T> {
    private pathToNameMap: { [path: string] : string};

    constructor(@IoC.Inject private readonly runtimeConfiguration: IRuntimeConfiguration) {
        super();
    }

    public router: Router5;

    public listen<K extends keyof T>(route: K, handler: (params: T[K]) => void, cleanHandler?: () => void) {
        const checkMatch = (matchedRoute: string) => {
            const targetRouteName = this.pathToNameMap[route as string];
            return matchedRoute === targetRouteName;
        };

        const unsubscribeList = [];

        if (cleanHandler) {
            unsubscribeList.push(this.listenOthers(route, cleanHandler));
        }
        unsubscribeList.push(this.listenInner(checkMatch, handler));

        return () => {
            unsubscribeList.forEach(x => x());
        };
     }

    public listenAll<K extends keyof T>(routes: K[], handler: (params: T[K]) => void): () => void {
        const checkMatch = (matchedRoute: string) => {
            let matchFound = false;
            for (const route of routes) {
                if (this.pathToNameMap[route as string] === matchedRoute) {
                    matchFound = true;
                }
            }

            return matchFound;
        };

        return this.listenInner(checkMatch, handler);
    }

    public listenOthers<K extends keyof T>(route: K, handler: () => void): () => void {
        const checkMatch = (matchedRoute: string) => {
            const targetRouteName = this.pathToNameMap[route as string];
            return matchedRoute !== targetRouteName;
        };

        return this.listenInner(checkMatch, handler);
    }

    public navigate<K extends keyof T>(route: K, params: T[K]) {
        if (!this.router) {
            throw new Error('unable to listen to route changes. Router not initialized.');
        }

        this.router.navigate(this.pathToNameMap[route as string], params);
    }

    public start() {
        if (!this.router) {
            throw new Error('unable to listen to route changes. Router not initialized.');
        }

        this.router.start();
    }

    public initialize(routesTable: { [routeName: string]: keyof T }) {
        if (this.router) {
            throw new Error('unable to re-initialize router');
        }

        this.pathToNameMap = {};

        const nodes = [];
        for (const routeName of Object.keys(routesTable)) {
            const path = routesTable[routeName] as string;

            nodes.push({
                name: routeName,
                path: path,
            });

            this.pathToNameMap[path] = routeName;
        }

        // in order to preserve query string params
        // queryParamsMode may be changed to 'read' parameters from query string but the strip them out after route is processed
        // this would make route looks cleaner, but will cause information lost. Consider.
        this.router = createRouter(nodes, {queryParamsMode: 'loose'});

        this.router.usePlugin(
            browserPlugin({base: this.runtimeConfiguration.getBaseUrl()})
        );
    }

    /**
     * part of internal router implementation, interface remains strongly typed
     *
     */
    private listenInner(
        checkMatch: (matchedRoute: string) => boolean,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handler: (params: any) => void,
    ) {
        if (!this.router) {
            throw new Error('unable to listen to route changes. Router not initialized.');
        }

        // in case listener is attached to currently active route - execute it immediately
        // const targetRouteName = this.pathToNameMap[targetRoute as string];
        const currentState = this.router.getState();
        if (currentState && checkMatch(currentState.name)) {
            handler(currentState.params);
        }

        // then subscribe to route changes
        return this.router.subscribe((state: SubscribeState) => {
            if (checkMatch(state.route.name)) {
                handler(state.route.params);
            }
        }) as Unsubscribe;
    }
}

export { Router };